import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, useTheme } from '@mui/material';
import ErrorHandlingLayout from '@sharedCR/common/ErrorHandlingLayout';
import {
  checkIsInvalidProgramStatus,
  handleNotAcceptableResponse,
} from 'app/pages/loanApplication/LoanApplication.utils';
import { checkIsTabHidden } from 'app/pages/system/autoLockRules/AutoLockRules.utils';
import { runInPendingChangesOperation } from 'app/services/pendingChangesOperation';
import { NoAvailableMessageContent } from 'app/shared/components/common/NoAvailableMessage';
import { CUSTOM_FIELDS_PARTIAL_SAVE_MESSAGE } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.constants';
import { buildCustomFieldsPendingChangesNode } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.utils';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';
import { CONFLICT_TYPE, HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { PAGE_CODES } from 'constants/pageCodes';
import { SYSTEM_PROPERTIES } from 'constants/properties';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import useLoanId from 'hooks/useLoanId';
import useRemoteFieldValueSync from 'hooks/useRemoteFieldValueSync';
import useTruncatedGeneral from 'hooks/useTruncatedGeneral';
import { getDefaultErrorMessage } from 'utils/axios';
import { jsonStringify } from 'utils/json';
import {
  clearEntitySideDataFromEmptyData,
  getInvalidTabsValidationMessage,
} from 'utils/loanApplication';
import { getConfigInfo } from 'utils/loanManagement';
import { logger } from 'utils/logger';

import { getMasterPermissionsToPage, selectMasterCompanyId } from 'store/auth/selectors';
import { selectCompanyPropertyByPropertyName } from 'store/companies/selectors';
import {
  closeDialogAction,
  openConfirmationDialogAction,
  showMessageAction,
} from 'store/global/actions';
import {
  getHousingFinanceAgenciesAction,
  getPropertyEnrichmentAction,
  getUsedLienPrioritiesAction,
  resetPropertyEnrichmentAction,
  setInvalidTabsAction,
} from 'store/loanApplications/actions';
import {
  selectChartLegend,
  selectChattelDetails,
  selectCommonInfoLoanApplication,
  selectHiddenFieldIds,
  selectHiddenPageIds,
  selectHousingFinanceAgenciesOptions,
  selectIsChattelLoanFromPropertyAndLoanSummary,
  selectIsHelocPurposePurchaseFromPropertyAndLoanSummary,
  selectIsLoanNoPpeEngineType,
  selectIsLoanOptimalBlueOrSifterEngineType,
  selectIsLoanPurposeAdditionalLoanFromPropertyAndLoanSummary,
  selectIsPurchaseLoanPurposeFromApplication,
  selectIsRefinanceLoanPurposeFromApplication,
  selectLoanContactsView,
  selectLoanFees,
} from 'store/loanApplications/selectors';
import { selectPricingCustomFields } from 'store/pricing/selectors';

import { NEW_RECORD_ID } from '../../PropertyAndLoan.contacts';
import {
  ENTITIES_WITH_DYNAMIC_SECTIONS,
  ENTITY_DYNAMIC_SECTION_CONTENT_MAP,
  ENTITY_SECTION_CONTENT_MAP,
  ENTITY_SECTION_FETCH_DATA_ACTION_MAP,
  ENTITY_SECTION_SAVE_ACTION_MAP,
  ENTITY_TYPE_HREF_MAP,
  getEntityFieldsMap,
  getNavigationTabsPropsByEntityType,
} from '../EditModal.constants';

import {
  DELETED_ENTITY_SECTION_MESSAGE_MAP,
  ENTITY_SECTION_GET_REQUEST_BODY_FUNCTION_MAP,
  ENTITY_SECTION_NORMALIZE_DATA_FUNCTION_MAP,
  ENTITY_SECTION_NORMALIZE_PENDING_CHANGES_FUNCTION_MAP,
  ENTITY_SECTION_PENDING_CHANGES_SELECTOR_MAP,
} from './Content.constants';
import { getWrapperStyles } from './Content.styles';
import { optionalVisibleFieldsTriggers } from './Content.utils';
import useCustomFieldsForDrawer from './hooks/useCustomFieldsForDrawer';
import useRedesignedLoanFeesFetch from './hooks/useRedesignedLoanFeesFetch';
import {
  checkIsChattelRefinance,
  getChattelContractAmount,
} from './MainContent/LoanInfo/LoanInfo.utils';
import usePropertyExpensesCalculations from './MainContent/PropertyExpenses/hooks/usePropertyExpensesCalculations';
import { ContentHeadline } from './MainContent/sharedComponents';

const Content = ({
  setIsFormChanged,
  isSaving,
  isLoading,
  setIsLoading,
  setIsSaving,
  onClose,
  additionalProps: {
    setPendingChanges,
    newRecords,
    newAddedInitialRecords,
    deletedRecords,
    deletedInitialRecords,
  },
  setIsSavingAllowed,
  isSavingAllowed,
  setIsNestedEntityEditActive,
  setOnSave,
}) => {
  const dispatch = useDispatch();
  const { applicationId, subtab, entityId: section } = useParams();
  const theme = useTheme();
  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();

  const isExternalLoanLockEnabled = useFeatureToggle(FeatureFlag.EnableExternalLoanLock);

  const loanId = useLoanId();

  const isPurchase = useSelector(selectIsPurchaseLoanPurposeFromApplication);
  const isRefinance = useSelector(selectIsRefinanceLoanPurposeFromApplication);
  const isAdditionalLoan = useSelector(selectIsLoanPurposeAdditionalLoanFromPropertyAndLoanSummary);
  const isHelocPurposePurchase = useSelector(
    selectIsHelocPurposePurchaseFromPropertyAndLoanSummary,
  );
  const pendingChanges = useSelector(ENTITY_SECTION_PENDING_CHANGES_SELECTOR_MAP[subtab]);
  const isLoanNoPpeEngineType = useSelector(selectIsLoanNoPpeEngineType);
  const { loanType } = useSelector(selectCommonInfoLoanApplication);
  const housingFinanceAgenciesOptions = useSelector(selectHousingFinanceAgenciesOptions, _.isEqual);
  const isOptimalBlueOrSifterEngineType = useSelector(selectIsLoanOptimalBlueOrSifterEngineType);
  const contactsView = useSelector(selectLoanContactsView);
  const { purchasePrice } = useSelector(selectChartLegend);
  const loanFees = useSelector(selectLoanFees);
  const isChattelLoanSummary = useSelector(selectIsChattelLoanFromPropertyAndLoanSummary);
  const chattelDetails = useSelector(selectChattelDetails);
  // LEN-127: the loaded chattel down payment / total are the baseline the Loan Info pending changes compare to.
  const chattelContractAmount = isChattelLoanSummary
    ? getChattelContractAmount(chattelDetails)
    : null;
  const isChattelRefinance = isChattelLoanSummary && checkIsChattelRefinance(chattelDetails);

  const shouldShowOBCustomFieldsSection =
    isOptimalBlueOrSifterEngineType && isExternalLoanLockEnabled;

  const { isViewAllowed: shouldShowLoanRolesSection } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_LOAN_ROLE_ASSIGNEMENT),
  );
  const { isViewAllowed: shouldShowLoanTagsSection } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_LOAN_TAG_ASSIGNEMENT),
  );
  const {
    isViewAllowed: shouldShowProfitAndLossSection,
    isManageAllowed: isProfitAndLossManageAllowed,
  } = useSelector(getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_PROFIT_AND_LOSS));

  const hiddenPageIds = useSelector(selectHiddenPageIds);
  const hiddenFieldIds = useSelector(selectHiddenFieldIds);
  const customFields = useSelector(selectPricingCustomFields);
  const masterCompanyId = useSelector(selectMasterCompanyId);
  const enrichmentProperty = useSelector(
    selectCompanyPropertyByPropertyName(
      masterCompanyId,
      SYSTEM_PROPERTIES.ENABLE_PROPERTY_ENRICHMENT,
    ),
  );
  const isEnrichmentEnabled = enrichmentProperty?.propertyValue === 'true';

  const [config, setConfig] = useState({ groups: [] });
  const [responseData, setResponseData] = useState(null);
  const [applicationData, setApplicationData] = useState({});
  const [entitySideData, setEntitySideData] = useState({});
  const [initialEntitySideData, setInitialEntitySideData] = useState({});
  const [isError, setIsError] = useState(false);
  const [formattedPendingChanges, setFormattedPendingChanges] = useState({});
  const [isSectionSavingAllowed, setIsSectionSavingAllowed] = useState(true);

  const formDataRef = useRef({});
  const pendingChangesRef = useRef(pendingChanges);

  const isNewRecord = newRecords.includes(section) || newAddedInitialRecords.includes(section);
  const isDeletedRecord =
    deletedRecords.includes(section) || deletedInitialRecords.includes(section);
  const deletedMessage = DELETED_ENTITY_SECTION_MESSAGE_MAP[subtab];

  const isPendingChangesHighlightDisabled = (isLoading && !isSaving) || isNewRecord;

  const isExternalRequestType = isOptimalBlueOrSifterEngineType;
  const isLockExtensionsV2 = isExternalLoanLockEnabled;

  const { title, iconName, SectionContent } = ENTITIES_WITH_DYNAMIC_SECTIONS.includes(subtab)
    ? ENTITY_DYNAMIC_SECTION_CONTENT_MAP[subtab]
    : (ENTITY_SECTION_CONTENT_MAP[subtab]?.[section] ?? {});

  useEffectSafeStrictMode(() => {
    dispatch(getUsedLienPrioritiesAction({ applicationId })).catch(Error);
  }, [dispatch, applicationId]);

  useEffect(() => {
    setEntitySideData(clearEntitySideDataFromEmptyData);
  }, [section]);

  // Chattel details has no cross-user pending-changes feed and no backing row until the first
  // save (see getChattelDetailsAction's 404 handling), so an empty applicationData is its normal
  // "no data yet" state -- not "still loading" -- and must not skip config/schema registration the
  // way it correctly does for the other entity types here.
  const isChattel = subtab === ENTITY_TYPE_HREF_MAP.CHATTEL;

  const setEntityConfig = useCallback(
    (config, isReplaceAll = false) => {
      if (_.isEmpty(applicationData) && !isNewRecord && !isChattel) {
        return;
      }

      setConfig(prev => {
        const newConfigKeys = config.groups.map(({ key }) => key);

        return {
          groups: isReplaceAll
            ? config.groups
            : [
                ...(prev?.groups ?? []).filter(({ key }) => !newConfigKeys.includes(key)),
                ...config.groups,
              ],
        };
      });
    },
    [applicationData, isNewRecord, isChattel],
  );

  const customFieldsController = useCustomFieldsForDrawer({ subtab, applicationId });
  const {
    isCapable: isCustomFieldsCapableSubtab,
    formValues: customFieldsInitialValues,
    pendingChangesByDefinition: customFieldsPendingChanges,
  } = customFieldsController;

  const applicationDataWithLoanData = useMemo(
    () => ({
      ...applicationData,
      customFields: customFieldsInitialValues,
    }),
    [applicationData, customFieldsInitialValues],
  );

  const { initialFormValues, schema, fieldsConfig, observableFieldsNames } = useMemo(
    () =>
      getConfigInfo({
        applicationData: applicationDataWithLoanData,
        config,
        isNestedForm: true,
      }),
    [applicationDataWithLoanData, config],
  );

  const {
    form,
    initialForm,
    userEditedFieldNamesRef,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    setMultipleInForm,
    isFormChanged,
    validateField,
    validateForm,
    setInitialFormValues,
    setForm,
    staticValidateField,
    setValidationErrors,
    setExcludedValidationPaths,
    formPendingChanges,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  const {
    remotePendingChanges,
    getCleanFormForSave,
    restoreRemoteInjectedValues,
    remoteInjectedFormKeys,
  } = useRemoteFieldValueSync({
    config,
    isNestedForm: true,
    setForm,
    setInitialFormValues,
  });

  useEffect(() => {
    setIsSavingAllowed(isSectionSavingAllowed);
  }, [setIsSavingAllowed, isSectionSavingAllowed]);

  useEffect(() => {
    const expandedKeys = new Set(remoteInjectedFormKeys);

    optionalVisibleFieldsTriggers.forEach(({ fieldName, dependents }) => {
      if (expandedKeys.has(fieldName)) {
        dependents.forEach(dep => expandedKeys.add(dep));
      }
    });

    setExcludedValidationPaths(expandedKeys);
  }, [remoteInjectedFormKeys, setExcludedValidationPaths]);

  const validationErrorsRef = useRef(validationErrors);

  usePropertyExpensesCalculations({
    form,
    applicationData,
    entitySideData,
    setInForm,
    setMultipleInForm,
    subtab,
  });

  useEffect(() => {
    const allDeletedRecords = [...deletedRecords, ...deletedInitialRecords];

    setConfig(prev => {
      const { groups, ...restConfigFields } = prev;

      const updatedGroups = groups.filter(group => {
        const groupId = group.key.slice(0, allDeletedRecords[0]?.length);

        return groupId.startsWith(NEW_RECORD_ID)
          ? !!newRecords.find(newRecordId => groupId.startsWith(newRecordId))
          : !allDeletedRecords.includes(groupId);
      });

      return {
        ...restConfigFields,
        groups: updatedGroups,
      };
    });

    setForm(prev => {
      const keysToPick = Object.keys(prev).filter(id =>
        id.startsWith(NEW_RECORD_ID) ? newRecords.includes(id) : !allDeletedRecords.includes(id),
      );

      return _.pick(prev, keysToPick);
    });
  }, [newRecords, deletedRecords, deletedInitialRecords, setForm]);

  useEffect(() => {
    if (!responseData) {
      return;
    }

    const normalizeFunction = ENTITY_SECTION_NORMALIZE_DATA_FUNCTION_MAP[subtab];

    const normalizedApplicationData = normalizeFunction({
      data: responseData,
      isPurchase,
      isRefinance,
      isAdditionalLoan,
      isHelocPurposePurchase,
      initialPendingChanges: pendingChangesRef.current,
      shouldShowLoanRolesSection,
      shouldShowLoanTagsSection,
      shouldShowProfitAndLossSection,
      shouldShowOBCustomFieldsSection,
    });

    setApplicationData(normalizedApplicationData);
  }, [
    shouldShowOBCustomFieldsSection,
    isAdditionalLoan,
    isHelocPurposePurchase,
    isPurchase,
    isRefinance,
    responseData,
    shouldShowLoanRolesSection,
    shouldShowLoanTagsSection,
    shouldShowProfitAndLossSection,
    subtab,
  ]);

  const fetchData = useCallback(() => {
    if (!applicationId) {
      return;
    }

    setIsError(false);
    setIsLoading(true);

    const action = ENTITY_SECTION_FETCH_DATA_ACTION_MAP[subtab];

    const shouldFetchSellSideLock =
      subtab === ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK && shouldShowProfitAndLossSection && !!loanId;

    dispatch(action({ applicationId, ...(shouldFetchSellSideLock && { loanId }) }))
      .then(({ payload }) => {
        setResponseData(payload);
      })
      .catch(e => {
        logger.error(e);
        setIsError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [setIsLoading, subtab, dispatch, applicationId, loanId, shouldShowProfitAndLossSection]);

  useEffect(() => {
    if (isPendingChangesHighlightDisabled && !isNewRecord) {
      return;
    }

    const normalizedPendingChanges = ENTITY_SECTION_NORMALIZE_PENDING_CHANGES_FUNCTION_MAP[subtab]({
      initialPendingChanges: pendingChanges || {},
      formPendingChanges,
      applicationData,
      entitySideData,
      isPurchase,
      isAdditionalLoan,
      isHelocPurposePurchase,
      shouldShowLoanRolesSection,
      shouldShowProfitAndLossSection,
      shouldShowLoanTagsSection,
      initialEntitySideData,
      housingFinanceAgenciesOptions,
      loanType,
      isLoanNoPpeEngineType,
      shouldShowOBCustomFieldsSection,
      newRecords,
      newAddedInitialRecords,
      deletedRecords,
      purchasePrice,
      loanFees,
      chattelContractAmount,
      isChattelRefinance,
    });

    const mergedWithRemote = _.merge({}, normalizedPendingChanges, remotePendingChanges);
    const customFieldsNode = buildCustomFieldsPendingChangesNode(customFieldsPendingChanges);
    const mergedWithCustomFields = _.merge({}, mergedWithRemote, {
      customFields: customFieldsNode,
    });

    setFormattedPendingChanges(mergedWithCustomFields);
    setPendingChanges(mergedWithCustomFields);
  }, [
    shouldShowOBCustomFieldsSection,
    isPendingChangesHighlightDisabled,
    isPurchase,
    isAdditionalLoan,
    isHelocPurposePurchase,
    applicationData,
    setPendingChanges,
    pendingChanges,
    formPendingChanges,
    initialEntitySideData,
    entitySideData,
    subtab,
    shouldShowLoanRolesSection,
    shouldShowLoanTagsSection,
    housingFinanceAgenciesOptions,
    shouldShowProfitAndLossSection,
    loanType,
    isLoanNoPpeEngineType,
    newRecords,
    newAddedInitialRecords,
    deletedInitialRecords,
    deletedRecords,
    isNewRecord,
    remotePendingChanges,
    customFieldsPendingChanges,
    purchasePrice,
    loanFees,
    chattelContractAmount,
    isChattelRefinance,
  ]);

  useEffect(() => {
    validationErrorsRef.current = validationErrors;
  }, [validationErrors]);

  useEffect(() => {
    pendingChangesRef.current = pendingChanges;
  }, [pendingChanges]);

  useEffect(() => {
    const hasProgramsSearchContext =
      _.get(entitySideData, 'programsAndPricingContext') &&
      !_.get(entitySideData, 'selectedProgram');
    const entitySideDataWoMeta = hasProgramsSearchContext
      ? _.omit(entitySideData, 'programsAndPricingContext')
      : entitySideData;

    const isSideDataEqual = _.isEqual(initialEntitySideData, entitySideDataWoMeta);
    const isSomeOfRecordsAddedOrDeleted = !!newRecords.length || !!deletedRecords.length;

    setIsFormChanged(isFormChanged || !isSideDataEqual || isSomeOfRecordsAddedOrDeleted);
  }, [
    entitySideData,
    initialEntitySideData,
    isFormChanged,
    setIsFormChanged,
    newRecords,
    deletedRecords,
  ]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema, schema]);

  useEffect(() => {
    setInitialFormValues(initialFormValues);
    setForm(form => _.merge({}, _.cloneDeep(initialFormValues), form));
  }, [initialFormValues, setForm, setInitialFormValues]);

  useEffect(() => {
    if (!applicationData) {
      return;
    }

    formDataRef.current = form;
  }, [form, applicationData]);

  useEffectSafeStrictMode(() => {
    fetchData();
  }, [fetchData]);

  useRedesignedLoanFeesFetch({ dispatch, loanId, setIsLoading, subtab });

  useEffectSafeStrictMode(() => {
    if (subtab !== ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK || housingFinanceAgenciesOptions.length) {
      return;
    }

    setIsLoading(true);
    dispatch(getHousingFinanceAgenciesAction()).finally(() => setIsLoading(false));
  }, [dispatch, housingFinanceAgenciesOptions, subtab, setIsLoading]);

  useEffectSafeStrictMode(() => {
    if (subtab !== ENTITY_TYPE_HREF_MAP.PROPERTY_INFO || !isEnrichmentEnabled) {
      return;
    }

    if (_.isEmpty(applicationData)) {
      return;
    }

    const reapiPropertyId = applicationData?.reapiPropertyId;
    const street = applicationData?.street;
    const city = applicationData?.city;
    const state = applicationData?.state;
    const zip = applicationData?.zip;
    const propertyAddress =
      street && city && state && zip ? `${street}, ${city}, ${state} ${zip}` : null;

    if (!reapiPropertyId && !propertyAddress) {
      return;
    }

    dispatch(getPropertyEnrichmentAction({ reapiPropertyId, address: propertyAddress }));

    return () => {
      dispatch(resetPropertyEnrichmentAction());
    };
  }, [dispatch, subtab, isEnrichmentEnabled, applicationData]);

  const prevInvalidSectionsRef = useRef([]);

  useEffect(() => {
    const fieldsMap = getEntityFieldsMap({ entityType: subtab, validationErrors });
    const formInvalidSections = _.keys(fieldsMap);

    const hasNewInvalidSections =
      formInvalidSections.length &&
      !_.isEqual(_.sortBy(formInvalidSections), _.sortBy(prevInvalidSectionsRef.current));

    if (hasNewInvalidSections && !formInvalidSections.includes(section)) {
      const tabs = formInvalidSections.map(invalidSection => {
        const tabProps = getNavigationTabsPropsByEntityType({ entityType: subtab, contactsView })[
          invalidSection
        ];

        return tabProps?.displayName ?? tabProps ?? '-';
      });

      const message = getInvalidTabsValidationMessage(tabs);

      dispatch(showMessageAction({ message, variant: 'error', id: jsonStringify(tabs) }));
    }

    prevInvalidSectionsRef.current = formInvalidSections;

    dispatch(setInvalidTabsAction(formInvalidSections));
  }, [dispatch, validationErrors, subtab, section, contactsView]);

  const finalizeWithCustomFieldsSave = useCallback(async () => {
    if (!isCustomFieldsCapableSubtab) {
      return true;
    }

    try {
      await customFieldsController.save();

      return true;
    } catch {
      dispatch(
        showMessageAction({
          message: CUSTOM_FIELDS_PARTIAL_SAVE_MESSAGE,
          variant: 'error',
        }),
      );

      return false;
    }
  }, [dispatch, isCustomFieldsCapableSubtab, customFieldsController]);

  const onSave = useCallback(
    async ({ isProceedWithRateSelection = false, withExtensionLimitOverride = false } = {}) => {
      if (!(await validateForm({ noFormChangedValidate: true }))) {
        return;
      }

      setIsLoading(true);

      const saveAction = ENTITY_SECTION_SAVE_ACTION_MAP[subtab];
      const initialFormForSave = restoreRemoteInjectedValues(initialForm);
      const body = ENTITY_SECTION_GET_REQUEST_BODY_FUNCTION_MAP[subtab]({
        form: getCleanFormForSave(formDataRef.current),
        initialForm: initialFormForSave,
        userEditedFieldNames: userEditedFieldNamesRef.current,
        entitySideData,
        initialEntitySideData,
        applicationData,
        isLoanNoPpeEngineType,
        withExtensionLimitOverride,
        loanType,
        isExternalRequestType,
        isLockExtensionsV2,
        isProfitAndLossManageAllowed,
        customFields,
        shouldShowOBCustomFieldsSection,
        newRecords,
        deletedRecords,
      });

      const isLoanAndLockModal = subtab === ENTITY_TYPE_HREF_MAP.LOAN_AND_LOCK;
      const params = {
        applicationId,
        loanId,
        ...body,
        silent: isLoanAndLockModal,
      };

      return runInPendingChangesOperation(() =>
        dispatch(saveAction(params)).then(async () => {
          const isCustomFieldsSaved = await finalizeWithCustomFieldsSave();

          if (isCustomFieldsSaved) {
            setTimeout(onClose);
          }
        }),
      )
        .catch(({ response }) => {
          if (isLoanAndLockModal) {
            const { conflictType, message = getDefaultErrorMessage({ response }) } =
              response?.data ?? {};

            const isInvalidProgram = checkIsInvalidProgramStatus({ response });

            const isConflictResponse = response?.status === HTTP_RESPONSE_STATUS_CODES.CONFLICT;

            const isLockExtensionLimitExceed =
              isConflictResponse && conflictType === CONFLICT_TYPE.LOCK_EXTENSION_LIMIT_EXCEEDED;

            if (!isInvalidProgram && !isLockExtensionLimitExceed) {
              dispatch(showMessageAction({ message, variant: 'error' }));
            }

            if (isLockExtensionLimitExceed) {
              const withExtensionLimitOverride = true;

              const params = {
                message: 'Confirm extension override',
                description: message,
                onAccept: () => {
                  setIsSaving(true);
                  onSave({ isProceedWithRateSelection, withExtensionLimitOverride }).finally(() =>
                    setIsSaving(false),
                  );
                },
                onAcceptButtonName: 'Accept',
              };

              return dispatch(openConfirmationDialogAction(params));
            }

            if (isInvalidProgram) {
              return handleNotAcceptableResponse({
                response,
                actionData: params,
                dispatch,
                onAcceptAction:
                  (...args) =>
                  () => {
                    setIsSaving(true);

                    return runInPendingChangesOperation(() =>
                      dispatch(saveAction(...args)).then(async () => {
                        const isCustomFieldsSaved = await finalizeWithCustomFieldsSave();

                        dispatch(closeDialogAction());

                        if (isCustomFieldsSaved) {
                          setTimeout(onClose);
                        }
                      }),
                    ).finally(() => setIsSaving(false));
                  },
                isSaveWithoutProgramHidden: true,
              });
            }
          }

          logger.error(response);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [
      validateForm,
      setIsLoading,
      subtab,
      getCleanFormForSave,
      restoreRemoteInjectedValues,
      initialForm,
      userEditedFieldNamesRef,
      entitySideData,
      initialEntitySideData,
      applicationData,
      isLoanNoPpeEngineType,
      loanType,
      isExternalRequestType,
      isLockExtensionsV2,
      applicationId,
      loanId,
      dispatch,
      onClose,
      setIsSaving,
      isProfitAndLossManageAllowed,
      customFields,
      shouldShowOBCustomFieldsSection,
      newRecords,
      deletedRecords,
      finalizeWithCustomFieldsSave,
    ],
  );

  useEffect(() => {
    setOnSave(() => onSave);
  }, [setOnSave, onSave]);

  if (checkIsTabHidden(section, hiddenPageIds, hiddenFieldIds)) {
    return <NoAvailableMessageContent className="mt-24" message="Page is hidden" />;
  }

  return (
    <Box sx={getWrapperStyles(theme)}>
      {title && <ContentHeadline iconName={iconName} title={title} />}

      <ErrorHandlingLayout isError={isError} onClick={fetchData}>
        <Box onMouseLeave={onResetTooltipState} onMouseOver={onMouseOver}>
          <TruncatedProvider />

          {!isDeletedRecord ? (
            <SectionContent
              applicationData={applicationData}
              className="form-wrapper"
              customFieldsController={customFieldsController}
              customFieldsPendingChanges={customFieldsPendingChanges}
              disabledPendingChangesHighlight={isPendingChangesHighlightDisabled}
              entitySideData={entitySideData}
              form={form}
              handleChange={handleChange}
              initialEntitySideData={initialEntitySideData}
              initialFormValues={initialFormValues}
              isDeletedRecord={isDeletedRecord}
              isFormChanged={isFormChanged}
              isLoading={isLoading}
              isSaving={isSaving}
              isSavingAllowed={isSavingAllowed}
              pendingChanges={formattedPendingChanges}
              setApplicationData={setApplicationData}
              setConfig={setConfig}
              setEntityConfig={setEntityConfig}
              setEntitySideData={setEntitySideData}
              setForm={setForm}
              setInForm={setInForm}
              setInitialEntitySideData={setInitialEntitySideData}
              setInitialFormValues={setInitialFormValues}
              setIsFormChanged={setIsFormChanged}
              setIsLoading={setIsLoading}
              setIsNestedEntityEditActive={setIsNestedEntityEditActive}
              setIsSavingAllowed={setIsSectionSavingAllowed}
              setMultipleInForm={setMultipleInForm}
              setValidationErrors={setValidationErrors}
              staticValidateField={staticValidateField}
              validateField={validateField}
              validationErrors={validationErrors}
            />
          ) : (
            <NoAvailableMessageContent
              message={`Sorry but we could not find data${
                deletedMessage ? ` for this ${deletedMessage}` : ''
              }`}
            />
          )}
        </Box>
      </ErrorHandlingLayout>
    </Box>
  );
};

Content.propTypes = {
  isSaving: PropTypes.bool.isRequired,
  isSavingAllowed: PropTypes.bool.isRequired,
  setIsFormChanged: PropTypes.func.isRequired,
  setIsSaving: PropTypes.func.isRequired,
  setOnSave: PropTypes.func.isRequired,
  setIsNestedEntityEditActive: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  ...WithLoadingPropTypes,
};

export default memo(Content);
